import { C } from "../components/reporting/reportingStyles";

/**
 * Merchant pages (privacy policy, terms…). Mirrors the API's src/const/merchantPage.js — keep the
 * limits and slug rule in step, or the panel will accept what the server refuses.
 */

export { QUILL_MODULES, QUILL_FORMATS } from "./appBanners";

export const LIMITS = {
  title: 160,
  slug: 60,
  slugMin: 2,
  metaDescription: 320,
  heading: 160,
  accordionTitle: 200,
  tableCaption: 200,
  tableCell: 500,
  calloutTitle: 160,
  maxBlocks: 60,
  maxAccordionItems: 40,
  maxTableColumns: 12,
  maxTableRows: 100,
};

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Public base shown next to slugs before the API has answered: the API's /merchant-pages, or an
 * alias via REACT_APP_PAGES_BASE_URL. Display only — copy/open always use the API's publicUrl,
 * and screens prefer the API's merchant.publicBaseUrl when they have it.
 */
export const PAGES_BASE_URL = String(
  process.env.REACT_APP_PAGES_BASE_URL || `${String(process.env.REACT_APP_URL || "").replace(/\/+$/, "")}/merchant-pages`
).replace(/\/+$/, "");

export const BLOCK_TYPES = [
  { value: "richText", label: "Rich text", hint: "Paragraphs, lists, bold and links." },
  { value: "accordion", label: "Accordion", hint: "Questions that open on tap — FAQs, rights, long clauses." },
  { value: "table", label: "Table", hint: "Rows and columns of plain text, e.g. cancellation windows." },
  { value: "callout", label: "Callout", hint: "A highlighted box for a notice or warning." },
  { value: "contact", label: "Contact details", hint: "Email, phone, WhatsApp… taken from Company Configuration." },
  { value: "divider", label: "Divider", hint: "A horizontal line between parts of the page." },
];

export const BLOCK_TYPE_LABELS = Object.fromEntries(BLOCK_TYPES.map((t) => [t.value, t.label]));

export const CALLOUT_TONES = [
  { value: "info", label: "Info", color: "#3b82f6", bg: "#eff6ff" },
  { value: "warning", label: "Warning", color: "#f59e0b", bg: "#fffbeb" },
  { value: "success", label: "Success", color: "#22c55e", bg: "#f0fdf4" },
];

export const CONTACT_FIELDS = [
  { key: "email", label: "Email", source: "Support email, else Email" },
  { key: "phone", label: "Phone", source: "Phone" },
  { key: "whatsapp", label: "WhatsApp", source: "WhatsApp support" },
  { key: "address", label: "Address", source: "Address" },
  { key: "website", label: "Website", source: "Website" },
];

export const PLACEHOLDERS = [
  { token: "{{merchantName}}", hint: "Merchant name (Arabic name on Arabic pages)" },
  { token: "{{supportEmail}}", hint: "Support email from Company Configuration" },
  { token: "{{supportPhone}}", hint: "WhatsApp support, else phone" },
  { token: "{{website}}", hint: "Website from Company Configuration" },
  { token: "{{lastUpdated}}", hint: "The page's Last updated on date" },
];

export const SLUG_SUGGESTIONS = [
  { slug: "privacy-policy", title: "Privacy Policy" },
  { slug: "terms-and-conditions", title: "Terms and Conditions" },
  { slug: "refund-policy", title: "Refund Policy" },
  { slug: "faq", title: "FAQ" },
];

export const STATUS_META = {
  draft: { label: "Draft", color: C.textMed, bg: C.borderLight },
  published: { label: "Published", color: C.green, bg: C.greenLight },
};

/** "Privacy Policy!" → "privacy-policy". Diacritics dropped, anything else becomes a hyphen. */
export const slugify = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, LIMITS.slug)
    .replace(/-+$/g, "");

/** The reason a slug would be refused, or null. `merchant` only changes the example. */
export const slugError = (slug, { merchant = false, required = true } = {}) => {
  const value = String(slug || "").trim();
  if (!value) return required ? "A URL slug is required." : null;
  if (value.length < LIMITS.slugMin || value.length > LIMITS.slug || !SLUG_PATTERN.test(value)) {
    return `Use ${LIMITS.slugMin}–${LIMITS.slug} lowercase letters, numbers and single hyphens, e.g. ${merchant ? "shovel-coffee" : "privacy-policy"}.`;
  }
  return null;
};

/** True when Quill's markup has no visible text — "<p><br></p>" is its empty document. */
export const htmlIsEmpty = (html) =>
  !html || String(html).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() === "";

let idSeq = 0;
export const newBlockId = () => `b-${Date.now().toString(36)}${(idSeq += 1).toString(36)}`;

const pair = (en = null, ar = null) => ({ en, ar });

/** A fresh block of `type`, in the shape the API stores. */
export const newBlock = (type) => {
  const id = newBlockId();
  switch (type) {
    case "richText":
      return { id, type, heading: pair(), body: pair() };
    case "accordion":
      return { id, type, heading: pair(), allowMultiple: false, items: [{ id: `${id}-1`, title: pair(), body: pair() }] };
    case "table":
      return { id, type, heading: pair(), caption: pair(), columns: [pair("Column 1"), pair("Column 2")], rows: [[pair(), pair()]] };
    case "callout":
      return { id, type, heading: null, tone: "info", title: pair(), body: pair() };
    case "contact":
      return { id, type, heading: pair("Contact Us", "تواصل معنا"), fields: { email: true, phone: true, whatsapp: true, address: false, website: true } };
    case "divider":
    default:
      return { id, type: "divider", heading: null };
  }
};

export const emptyPage = () => ({
  slug: "",
  titleEn: "",
  titleAr: "",
  sections: { version: 1, blocks: [] },
  showToc: true,
  lastUpdatedOn: "",
  metaDescriptionEn: "",
  metaDescriptionAr: "",
  noindex: false,
});

/** The editable fields of a loaded page, normalised so dirty-checking compares like with like. */
export const formFromPage = (p) => ({
  slug: p?.slug || "",
  titleEn: p?.titleEn || "",
  titleAr: p?.titleAr || "",
  sections: p?.sections || { version: 1, blocks: [] },
  showToc: p?.showToc !== false,
  lastUpdatedOn: p?.lastUpdatedOn || "",
  metaDescriptionEn: p?.metaDescriptionEn || "",
  metaDescriptionAr: p?.metaDescriptionAr || "",
  noindex: Boolean(p?.noindex),
});

/** Audit labels for the page history panel (AuditLogPanel, module "pages"). */
export const PAGE_AUDIT_ACTION_LABELS = {
  create: "Created",
  update: "Edited",
  publish: "Published",
  unpublish: "Unpublished",
  duplicate: "Duplicated",
  delete: "Deleted",
};

export const PAGE_AUDIT_FIELD_LABELS = {
  slug: "URL slug",
  titleEn: "Title (EN)",
  titleAr: "Title (AR)",
  sections: "Sections",
  showToc: "Table of contents",
  lastUpdatedOn: "Last updated on",
  metaDescriptionEn: "Meta description (EN)",
  metaDescriptionAr: "Meta description (AR)",
  noindex: "Hidden from search engines",
  status: "Status",
  blockCount: "Sections",
};

export const pageAuditResolve = (field, value) => {
  if (field === "sections") return `${value?.blocks?.length ?? 0} section(s)`;
  if (field === "status") return STATUS_META[value]?.label || value;
  return undefined;
};
